import { useMemo } from "react";
import { BUTTON_LABELS } from "@miplatform/constants";
import { CalendarEventType, ICalendarSet } from "@miplatform/types";
import {
  mdiChevronDown,
  mdiContentCopy,
  mdiPencilOutline,
  mdiTrashCanOutline,
} from "@mdi/js";
import { useTheme } from "@mui/material";
import { ConfigCard } from "../../components/ConfigCard/ConfigCard";
import { ConfigMenu } from "../../components/ConfigMenu/ConfigMenu";
import { CALENDAR_PAGE_CONFIG } from "../ConfigCalendarSetList.constants";
import { ICalendarSetData } from "../ConfigCalendarSetList.types";
import {
  getCalendarSetStatusActions,
  getCalendarSetStatusColor,
} from "../ConfigCalendarSetList.util";

export const ConfigCalendarSetCard = ({
  item: calendar,
  canUpdate = false,
  canDelete = false,
  navigateToDetails = () => {},
  onDuplicate = () => {},
  onUpdateStatus = () => {},
  onClick = () => {},
  onDelete,
}: ConfigCalendarSetCardProps) => {
  const {
    modifiedOn,
    createdOn,
    name,
    id,
    schedulerUsers,
    assignableUsers,
    workTemplateBases,
  } = calendar;
  const theme = useTheme();
  const totalUsers = useMemo(() => {
    const userIds = new Set<string>();
    schedulerUsers.forEach((user) => userIds.add(user.id));
    assignableUsers.forEach((user) => userIds.add(user.id));

    return userIds.size;
  }, [schedulerUsers, assignableUsers]);

  const actions = [];

  if (canUpdate) {
    actions.push({
      label: BUTTON_LABELS.EDIT,
      action: () => {
        navigateToDetails(id);
      },
      icon: mdiPencilOutline,
    });
    actions.push({
      label: BUTTON_LABELS.DUPLICATE,
      action: () => onDuplicate(calendar),
      icon: mdiContentCopy,
      color: theme.palette.primary.main,
    });
  }

  if (canDelete) {
    actions.push({
      label: BUTTON_LABELS.DELETE,
      action: () => onDelete(calendar),
      icon: mdiTrashCanOutline,
      color: theme.palette.red.main,
    });
  }

  return (
    <ConfigCard
      title={name}
      additionalTitleText={[
        `${totalUsers} User${totalUsers !== 1 ? "s" : ""}`,
        `${workTemplateBases.length} ${
          CALENDAR_PAGE_CONFIG[calendar.eventType || CalendarEventType.Order][
            workTemplateBases.length !== 1 ? "entityNamePlural" : "entityName"
          ]
        }`,
      ]}
      modifiedOn={modifiedOn}
      createdOn={createdOn}
      menuActions={actions}
      onClick={() => onClick(id)}
      customAction={
        <ConfigMenu
          data={getCalendarSetStatusActions(calendar, onUpdateStatus)}
          optionalElement={{
            size: "small",
            title: calendar.status,
            color: getCalendarSetStatusColor(calendar.status),
            endIcon: { path: mdiChevronDown },
            sx: { width: 85 },
          }}
        />
      }
    />
  );
};

interface ConfigCalendarSetCardProps {
  item: ICalendarSet;
  canUpdate?: boolean;
  canDelete?: boolean;
  navigateToDetails: (calendarId: string) => void;
  onClick?: (calendarId: string) => void;
  onDuplicate?: (calendar: ICalendarSet) => void;
  onUpdateStatus?: (
    calendar: Partial<ICalendarSet> | Partial<ICalendarSetData>
  ) => void;
  onDelete: (calendar: ICalendarSet) => void;
}
